import { useEffect, useRef } from 'react';
import { Card, Typography } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import {
  HELP_SECTIONS,
  findHelpSection,
  type HelpArticle,
  type HelpBlock,
} from '@shared/help';

const { Title, Text, Paragraph } = Typography;

/**
 * Разбирает **жирный** markdown в текст → массив React-узлов (strong внутри).
 * Простой парсер: делит по **…** и оборачивает нечётные сегменты в <strong>.
 */
function renderInline(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((seg, i) =>
    i % 2 === 1 ? <strong key={i}>{seg}</strong> : <span key={i}>{seg}</span>,
  );
}

/** Картинка статьи с подписью. Прячется, если файл ещё не залит (onError). */
function ArticleImage({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure style={{ margin: '16px 0' }}>
      <img
        src={`/getting-started/help/${src}`}
        alt={alt}
        style={{
          width: '100%',
          borderRadius: 12,
          border: '1px solid #E5E7EB',
          display: 'block',
        }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
      {caption && (
        <figcaption
          style={{
            marginTop: 8,
            fontSize: 13,
            lineHeight: 1.5,
            color: '#6B7280',
            fontStyle: 'italic',
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/** Рендер одного блока контента. */
function BlockView({ block }: { block: HelpBlock }) {
  switch (block.type) {
    case 'heading':
      return (
        <Title level={4} style={{ marginTop: 24, marginBottom: 8, fontWeight: 600 }}>
          {block.text}
        </Title>
      );
    case 'paragraph':
      return (
        <Paragraph style={{ marginBottom: 12, lineHeight: 1.7 }}>
          {renderInline(block.text)}
        </Paragraph>
      );
    case 'list':
      return (
        <ul style={{ marginBottom: 12, paddingLeft: 22 }}>
          {block.items.map((it, i) => (
            <li key={i} style={{ marginBottom: 6, lineHeight: 1.6 }}>
              {renderInline(it)}
            </li>
          ))}
        </ul>
      );
    case 'image':
      return <ArticleImage src={block.src} alt={block.alt} caption={block.caption} />;
    case 'faq':
      return (
        <div style={{ marginTop: 16 }}>
          <Title level={4} style={{ marginBottom: 12, fontWeight: 600 }}>
            Частые вопросы
          </Title>
          {block.items.map((qa, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {qa.q}
              </Text>
              <Paragraph style={{ marginBottom: 0, color: '#374151', lineHeight: 1.7 }}>
                {renderInline(qa.a)}
              </Paragraph>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

/** Одна статья: заголовок, саммари, блоки. С id-якорем для мини-оглавления. */
function ArticleView({ article }: { article: HelpArticle }) {
  return (
    <Card
      id={article.slug}
      style={{ marginBottom: 16, scrollMarginTop: 16 }}
    >
      <Title level={3} style={{ marginTop: 0, fontWeight: 500 }}>
        {article.title}
      </Title>
      {article.summary && (
        <Paragraph
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: '#F9FAFB',
            borderLeft: '3px solid #D1D5DB',
            borderRadius: 6,
            color: '#374151',
          }}
        >
          <Text strong>Коротко: </Text>
          {renderInline(article.summary)}
        </Paragraph>
      )}
      {article.blocks.map((b, i) => (
        <BlockView key={i} block={b} />
      ))}
    </Card>
  );
}

export function HelpSectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const section = slug ? findHelpSection(slug) : undefined;

  // Скролл наверх при смене раздела.
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [slug]);

  // Неизвестный раздел — вернём на обучение.
  useEffect(() => {
    if (slug && !section) {
      navigate('/getting-started', { replace: true });
    }
  }, [slug, section, navigate]);

  if (!section) return null;

  // Клик по статье в мини-оглавлении — скролл к её якорю.
  const scrollToArticle = (articleSlug: string) => {
    const el = document.getElementById(articleSlug);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ maxWidth: 820 }} ref={topRef}>
      <Title level={2} style={{ marginTop: 0 }}>
        {section.name}
      </Title>
      <Text type="secondary">
        Раздел базы знаний — {section.articles.length}{' '}
        {pluralArticles(section.articles.length)}.
      </Text>

      {/* Мини-оглавление раздела */}
      {section.articles.length > 1 && (
        <Card size="small" style={{ margin: '16px 0 24px' }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            В этом разделе
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {section.articles.map((a) => (
              <button
                key={a.slug}
                type="button"
                onClick={() => scrollToArticle(a.slug)}
                style={{
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '2px 0',
                  cursor: 'pointer',
                  color: '#2563EB',
                  fontSize: 14,
                }}
              >
                {a.title}
              </button>
            ))}
          </div>
        </Card>
      )}

      {section.articles.map((a) => (
        <ArticleView key={a.slug} article={a} />
      ))}
    </div>
  );
}

/** Склонение «статья» по числу. */
function pluralArticles(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'статья';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'статьи';
  return 'статей';
}

// Явно ссылаемся на HELP_SECTIONS, чтобы дерево импортов не терялось при tree-shaking
// (findHelpSection достаточно, но оставляем для читаемости витрины разделов).
void HELP_SECTIONS;
